/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const SMSNew = defineMessages({
  titleOne: {
    id: 'sms.SMSNew.titleOne',
    defaultMessage: 'Your customers are texting'
  },
  titleTwo: {
    id: 'sms.SMSNew.titleTwo',
    defaultMessage: 'Your assistant can answer'
  },
  description: {
    id: 'sms.SMSNew.description',
    defaultMessage: 'description'
  },
  instructions: {
    id: 'sms.SMSNew.instructions',
    defaultMessage: "What you'll need"
  },
  instructionsDescription: {
    id: 'sms.SMSNew.instructionsDescription',
    defaultMessage:
      "This integration requires some setup. Select Create to get all the details, but here's what to expect:"
  },
  instructionsItemOne: {
    id: 'sms.SMSNew.instructionsItemOne',
    defaultMessage:
      'Get a phone number with SMS capabilities through Twilio or IntelePeer. (MMS optional)'
  },
  instructionsItemTwo: {
    id: 'sms.SMSNew.instructionsItemTwo',
    defaultMessage:
      'In Watson Assistant, a webhook identifier will be generated for you.'
  },
  instructionsItemThree: {
    id: 'sms.SMSNew.instructionsItemThree',
    defaultMessage:
      "With your provider, you'll set your phone number's messaging to configure with webhooks and the generated identifier."
  },
  learnMore: {
    id: 'sms.SMSNew.learnMore',
    defaultMessage: '<link>Learn more about SMS</link>'
  },
  SelectProviderStep: {
    id: 'sms.SMSNew.SelectProviderStep',
    defaultMessage: 'Select SMS provider'
  },
  ConnectProviderStep: {
    id: 'sms.SMSNew.ConnectProviderStep',
    defaultMessage: 'Connect to SMS provider'
  },
  PhoneNumberStep: {
    id: 'sms.SMSNew.PhoneNumberStep',
    defaultMessage: 'Phone number'
  },
  WebhookStep: {
    id: 'sms.SMSNew.WebhookStep',
    defaultMessage: 'Webhook URI'
  }
});

export const SelectProviderStepMessages = defineMessages({
  title: {
    id: 'sms.SelectProviderStepMessages.title',
    defaultMessage: 'Select an SMS provider to use'
  },
  description: {
    id: 'sms.SelectProviderStepMessages.description',
    defaultMessage:
      'Give your assistant the ability to send and receive text messages with users.'
  }
});

export const ConnectProviderStepMessages = defineMessages({
  title: {
    id: 'sms.ConnectProviderStepMessages.title',
    defaultMessage: 'Connect to your SMS Provider'
  },
  tabTitle: {
    id: 'sms.ConnectProviderStepMessages.tabTitle',
    defaultMessage: 'SMS service provider'
  },
  description: {
    id: 'sms.ConnectProviderStepMessages.description',
    defaultMessage:
      'With your provider, your assistant is ready to send and receive SMS and MMS text messages. This integration can even pair with phone calls, enabling your assistant to send and receive texts with a caller. <link>Learn more</link>'
  },
  connectAccount: {
    id: 'sms.ConnectProviderStepMessages.connectAccount',
    defaultMessage: 'Connect to your {provider} account'
  },
  descriptionTwilio: {
    id: 'sms.ConnectProviderStepMessages.descriptionTwilio',
    defaultMessage:
      "To use SMS with Twilio, you have to get a Twilio phone number with SMS capabilities (MMS optional). In your <link>Twilio account</link>, set your phone number's messaging to configure with webhooks, which should be its default. Use this webhook uri as the address for incoming messages. Make sure it's set to HTTP POST."
  },
  descriptionIntelepeer: {
    id: 'sms.ConnectProviderStepMessages.descriptionIntelepeer',
    defaultMessage:
      'To setup with IntelePeer, you need an IntelePeer account first. Navigate to your IntelePeer account <link>here</link>.'
  },
  descriptionGenesys: {
    id: 'sms.ConnectProviderStepMessages.descriptionGenesys',
    defaultMessage:
      'Create your Genesys Cloud account or log in to an existing one with access to Genesys Architect to <link>complete SMS setup</link>, and have ready your Integration ID and the Value you set as the secret in the Credentials tab.'
  },
  descriptionCustom: {
    id: 'sms.ConnectProviderStepMessages.descriptionCustom',
    defaultMessage:
      'Use your own SMS provider by configuring the integration with your existing messaging infrastructure. This setup allows your assistant to authenticate and exchange messages through your custom middleware.'
  },
  connectBYOPAccount: {
    id: 'sms.CustomSMSAuthMessages.connectBYOPAccount',
    defaultMessage: 'Connect to your custom SMS provider (BYOP) account'
  }
});

export const IntelepeerAuthMessages = defineMessages({
  apiAuthTokenLabel: {
    id: 'sms.IntelepeerAuthMessages.apiAuthTokenLabel',
    defaultMessage: 'API Authentication token'
  },
  apiAuthTokenInfo: {
    id: 'sms.IntelepeerAuthMessages.apiAuthTokenInfo',
    defaultMessage:
      'In your IntelePeer account page, find your API Authentication token and paste here'
  },
  authTokenPlaceholder: {
    id: 'sms.IntelepeerAuthMessages.authTokenPlaceholder',
    defaultMessage: 'Enter API Authentication token'
  },
  authTokenRequired: {
    id: 'sms.IntelepeerAuthMessages.authTokenRequired',
    defaultMessage: 'API Authentication token is required.'
  },
  authTokenError: {
    id: 'sms.IntelepeerAuthMessages.authTokenError',
    defaultMessage: 'API Authentication token is invalid.'
  },
  authTokenNoWhitespaceError: {
    id: 'sms.IntelepeerAuthMessages.authTokenNoWhitespaceError',
    defaultMessage: 'Auth token cannot be empty'
  },
  accountSecretLabel: {
    id: 'sms.IntelepeerAuthMessages.accountSecretLabel',
    defaultMessage: 'Account secret'
  },
  accountSecretInfo: {
    id: 'sms.IntelepeerAuthMessages.accountSecretInfo',
    defaultMessage:
      'In your IntelePeer account page, find your Account secret and paste here'
  },
  accountSecretInvalid: {
    id: 'sms.IntelepeerAuthMessages.accountSecretInvalid',
    defaultMessage: 'Account secret must be between 8 and 64 characters.'
  },
  accountSecretPlaceholder: {
    id: 'sms.IntelepeerAuthMessages.accountSecretPlaceholder',
    defaultMessage: 'Enter Account secret'
  },
  accountSecretRequired: {
    id: 'sms.IntelepeerAuthMessages.accountSecretRequired',
    defaultMessage: 'Account secret is required.'
  },
  accountSecretLengthError: {
    id: 'sms.IntelepeerAuthMessages.accountSecretLengthError',
    defaultMessage: 'Account secret must be 8-64 characters.'
  },
  accountSecretMinCharError: {
    id: 'sms.IntelepeerAuthMessages.accountSecretMinCharError',
    defaultMessage: 'Account secret must be at least 8 characters.'
  },
  accountSecretMaxCharError: {
    id: 'sms.IntelepeerAuthMessages.accountSecretMaxCharError',
    defaultMessage: 'Account secret must be at most 64 characters.'
  },
  accountSecretError: {
    id: 'sms.IntelepeerAuthMessages.accountSecretError',
    defaultMessage: 'Account secret is invalid.'
  },
  accountSecretNoWhitespaceError: {
    id: 'sms.IntelepeerAuthMessages.authTokenNoWhitespaceError',
    defaultMessage: 'Account secret cannot be empty'
  }
});

export const GenesysSMSAuthMessages = defineMessages({
  stepOne: {
    id: 'sms.GenesysSMSAuthMessages.stepOne',
    defaultMessage:
      'On the Genesys Admin page under Integrations, go to OAuth and copy the {clientId} and {clientSecret}, and paste below'
  },
  clientId: {
    id: 'sms.GenesysSMSAuthMessages.clientId',
    defaultMessage: 'Client ID'
  },
  clientIdRequired: {
    id: 'sms.GenesysSMSAuthMessages.clientIdRequired',
    defaultMessage: 'Client ID is required'
  },
  nonEmptyClientId: {
    id: 'sms.GenesysSMSAuthMessages.nonEmptyClientId',
    defaultMessage: 'client ID cannot be empty'
  },
  clientSecret: {
    id: 'sms.GenesysSMSAuthMessages.clientSecret',
    defaultMessage: 'Client Secret'
  },
  clientSecretRequired: {
    id: 'sms.GenesysSMSAuthMessages.clientSecretRequired',
    defaultMessage: 'Client Secret is required'
  },
  nonEmptyClientSecret: {
    id: 'sms.GenesysSMSAuthMessages.nonEmptyClientSecret',
    defaultMessage: 'client Secret cannot be empty'
  },
  verificationToken: {
    id: 'sms.GenesysSMSAuthMessages.verificationToken',
    defaultMessage: 'Value for verification token'
  },
  verificationTokenRequired: {
    id: 'sms.GenesysSMSAuthMessages.verificationTokenRequired',
    defaultMessage: 'Verification token is required'
  },
  nonEmptyVerificationToken: {
    id: 'sms.GenesysSMSAuthMessages.nonEmptyVerificationToken',
    defaultMessage: 'Verification token cannot be empty'
  },
  apiUriLabel: {
    id: 'sms.GenesysSMSAuthMessages.apiUriLabel',
    defaultMessage: 'API URI'
  },
  apiUriPlaceHolder: {
    id: 'sms.GenesysSMSAuthMessages.apiUriPlaceHolder',
    defaultMessage: 'For example: For US East, https://api.mypurecloud.com'
  },
  apiUriRequired: {
    id: 'sms.GenesysSMSAuthMessages.apiUriRequired',
    defaultMessage: 'API URI is required'
  },
  nonEmptyApiUri: {
    id: 'sms.GenesysSMSAuthMessages.nonEmptyApiUri',
    defaultMessage: 'API URI cannot be empty'
  },
  stepTwo: {
    id: 'sms.GenesysSMSAuthMessages.stepTwo',
    defaultMessage:
      'Enter the value set for the {xWatsonGenesysVerificationToken} header field in the {webServicesActionData}.'
  },
  webServicesActionData: {
    id: 'sms.GenesysSMSAuthMessages.webServicesActionData',
    defaultMessage: 'Web Services action data'
  },
  stepThree: {
    id: 'sms.GenesysSMSAuthMessages.stepThree',
    defaultMessage:
      'On the Genesys API overview page, go to <strong> <link> API Server URI </link> </strong>, copy the URI for your region, and paste below.'
  }
});

export const CustomSMSAuthMessages = defineMessages({
  byopVerificationToken: {
    id: 'sms.CustomSMSAuthMessages.byopVerificationToken',
    defaultMessage: 'BYOP verification token'
  },
  verificationTokenLabel: {
    id: 'sms.CustomSMSAuthMessages.byopVerificationToken',
    defaultMessage: 'BYOP verification token'
  },
  verificationTokenPlaceholder: {
    id: 'sms.CustomSMSAuthMessages.verificationTokenPlaceholder',
    defaultMessage: `Enter your provider's verification token`
  },
  verificationTokenRequired: {
    id: 'sms.CustomSMSAuthMessages.byopVerificationToken',
    defaultMessage: 'BYOP verification token is required'
  },
  nonEmptyVerificationToken: {
    id: 'sms.CustomSMSAuthMessages.nonEmptyVerificationToken',
    defaultMessage: 'BYOP verification token cannot be empty'
  },
  apiUriLabel: {
    id: 'sms.CustomSMSAuthMessages.apiUriLabel',
    defaultMessage: 'API URI'
  },
  apiUriPlaceHolder: {
    id: 'sms.CustomSMSAuthMessages.apiUriPlaceHolder',
    defaultMessage: 'For example: For US East, https://api.mypurecloud.com'
  },
  apiUriRequired: {
    id: 'sms.CustomSMSAuthMessages.apiUriRequired',
    defaultMessage: 'API URI is required'
  },
  nonEmptyApiUri: {
    id: 'sms.CustomSMSAuthMessages.nonEmptyApiUri',
    defaultMessage: 'API URI cannot be empty'
  },
  apiUriByopPlaceHolder: {
    id: 'sms.CustomSMSAuthMessages.apiUriByopPlaceHolder',
    defaultMessage: 'https://host.domain.com/path/to/endpoint'
  },
  byopVerificationTokenStep: {
    id: 'sms.CustomSMSAuthMessages.apiUriByopPlaceHolder',
    defaultMessage: `Copy the auto-generated BYOP verification token and save it in some safe place.
    This token is used to authenticate inbound requests and should be included in all outbound API requests from your system to this integration.`
  },
  byopAPIURIStep: {
    id: 'sms.CustomSMSAuthMessages.byopAPIURIStep',
    defaultMessage: `Enter your API endpoint URL.
    Provide the fully qualified HTTPS URL where your server will receive outbound message requests. This URL must be accessible and able to handle POST requests containing message payloads.`
  }
});
